"use client";

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import Head from 'next/head';
import SearchPrompt from '../../components/ui/SearchPrompt';
import Sidebar from '../../components/layout/Sidebar';
import MobileNavigation from '../../components/layout/MobileNavigation';
import MobileHeader from '../../components/layout/MobileHeader';
import { useCheckout } from '../../context/CheckoutContext';

export default function RestaurantPage({ params }) {
  const router = useRouter();
  const { addToCart } = useCheckout();
  
  // Unwrap params Promise using React.use()
  const resolvedParams = React.use(params);
  
  // State for active menu category
  const [activeCategory, setActiveCategory] = useState('popular');
  // State to track if all opening hours are visible
  const [showAllHours, setShowAllHours] = useState(false);
  // Position for toast notification
  const [toastPosition, setToastPosition] = useState({ x: 0, y: 0 });
  // State to control mobile sidebar
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // State for cart functionality
  const [foodCart, setFoodCart] = useState([]);
  const [showCartModal, setShowCartModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedVariations, setSelectedVariations] = useState({});
  const [itemQuantity, setItemQuantity] = useState(1);
  const [basePrice, setBasePrice] = useState(0);
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [toast, setToast] = useState({ show: false, message: '' });
  
  // Get restaurant data based on slug
  const getRestaurantData = (slug) => {
    // In a real app, this would come from an API
    const restaurantMap = {
      'burger-king': {
        name: 'Burger King',
        rating: 4.5,
        deliveryTime: '25-35 min',
        priceRange: 'RsRs',
        categories: ['Fast Food', 'Burgers'],
        image: '🍔',
        bgColor: 'bg-amber-400',
        textColor: 'text-black',
        address: 'Shop #5, Food Street, Block 13-D, Gulshan-e-Iqbal, Karachi',
        phone: '+92 301 1234567',
        openingHours: {
          monday: '10:00 AM - 11:00 PM',
          tuesday: '10:00 AM - 11:00 PM',
          wednesday: '10:00 AM - 11:00 PM',
          thursday: '10:00 AM - 11:00 PM',
          friday: '10:00 AM - 12:00 AM',
          saturday: '10:00 AM - 12:00 AM',
          sunday: '11:00 AM - 10:00 PM'
        },
        description: 'Home of the Whopper! Burger King is an American multinational chain of hamburger fast food restaurants, known for flame-grilled burgers.',
        website: 'burgerking.pk',
        menuCategories: [
          { id: 'popular', name: 'Popular Items' },
          { id: 'burgers', name: 'Burgers' },
          { id: 'sides', name: 'Sides' },
          { id: 'drinks', name: 'Drinks & Beverages' },
          { id: 'desserts', name: 'Desserts' },
          { id: 'deals', name: 'Deals' }
        ],
        menu: {
          deals: [
            { id: 101, name: 'Family Bundle', price: 'Rs. 2500', description: 'Perfect for family gatherings. 2 Whoppers, 2 Chicken Royales, 4 small fries, and 4 soft drinks.', image: '🍔' },
            { id: 102, name: 'Whopper Wednesday', price: 'Rs. 650', description: 'Buy one Whopper and get one free every Wednesday.', image: '🍔' },
            { id: 103, name: 'King Box Meal', price: 'Rs. 950', description: 'Your choice of burger with fries, drink, and dessert.', image: '👑' }
          ],
          popular: [
            { id: 1, name: 'Whopper', price: 'Rs. 850', description: 'Our signature flame-grilled beef patty topped with juicy tomatoes, fresh lettuce, creamy mayonnaise, ketchup, crunchy pickles, and sliced white onions on a soft sesame seed bun.', image: '🍔' },
            { id: 2, name: 'Chicken Royale', price: 'Rs. 750', description: 'Crispy chicken fillet topped with fresh lettuce and creamy mayonnaise on a long sesame seed bun.', image: '🍗' },
            { id: 3, name: 'Double Whopper', price: 'Rs. 1050', description: 'Two flame-grilled beef patties topped with all the fresh ingredients.', image: '🍔' }
          ],
          burgers: [
            { id: 4, name: 'Whopper', price: 'Rs. 850', description: 'Our signature flame-grilled beef patty topped with juicy tomatoes, fresh lettuce, creamy mayonnaise, ketchup, crunchy pickles, and sliced white onions on a soft sesame seed bun.', image: '🍔' },
            { id: 5, name: 'Double Whopper', price: 'Rs. 1050', description: 'Two flame-grilled beef patties topped with all the fresh ingredients.', image: '🍔' },
            { id: 6, name: 'Cheese Whopper', price: 'Rs. 950', description: 'Our signature Whopper with an extra slice of cheese.', image: '🧀' },
            { id: 7, name: 'Chicken Whopper', price: 'Rs. 830', description: 'Flame-grilled chicken patty topped with fresh ingredients.', image: '🍗' }
          ],
          sides: [
            { id: 8, name: 'French Fries', price: 'Rs. 350', description: 'Golden, crispy, and delicious potato fries.', image: '🍟' },
            { id: 9, name: 'Onion Rings', price: 'Rs. 370', description: 'Crispy, golden onion rings.', image: '⭕' },
            { id: 10, name: 'Chicken Nuggets (6pcs)', price: 'Rs. 450', description: 'Bite-sized pieces of tender chicken in a crispy coating.', image: '🍗' }
          ],
          drinks: [
            { id: 11, name: 'Coca Cola', price: 'Rs. 150', description: 'Classic Coca Cola soft drink.', image: '🥤' },
            { id: 12, name: 'Sprite', price: 'Rs. 150', description: 'Refreshing lemon-lime flavored soft drink.', image: '🥤' },
            { id: 13, name: 'Fresh Lime', price: 'Rs. 200', description: 'Refreshing lime juice with mint.', image: '🍋' }
          ],
          desserts: [
            { id: 14, name: 'Chocolate Sundae', price: 'Rs. 300', description: 'Vanilla soft serve topped with chocolate sauce.', image: '🍨' },
            { id: 15, name: 'Apple Pie', price: 'Rs. 250', description: 'Warm apple pie with a flaky crust.', image: '🥧' }
          ]
        }
      },
      'pizza-hut': {
        name: 'Pizza Hut',
        rating: 4.2,
        deliveryTime: '30-45 min',
        priceRange: 'RsRsRs',
        categories: ['Pizza', 'Italian'],
        image: '🍕',
        bgColor: 'bg-red-600',
        textColor: 'text-white',
        address: '27 Main Boulevard, Gulberg III, Lahore',
        phone: '+92 302 9876543',
        openingHours: {
          monday: '11:00 AM - 12:00 AM',
          tuesday: '11:00 AM - 12:00 AM',
          wednesday: '11:00 AM - 12:00 AM',
          thursday: '11:00 AM - 12:00 AM',
          friday: '11:00 AM - 1:00 AM',
          saturday: '11:00 AM - 1:00 AM',
          sunday: '12:00 PM - 12:00 AM'
        },
        description: 'Pizza Hut is an American restaurant chain and international franchise founded in 1958 in Wichita, Kansas by Dan and Frank Carney. They serve their signature pan pizza and other dishes including pasta, breadsticks and desserts.',
        website: 'pizzahut.pk',
        menuCategories: [
          { id: 'popular', name: 'Popular Items' },
          { id: 'pizza', name: 'Pizzas' },
          { id: 'sides', name: 'Appetizers & Sides' },
          { id: 'drinks', name: 'Drinks' },
          { id: 'desserts', name: 'Desserts' },
          { id: 'deals', name: 'Deals' }
        ],
        menu: {
          deals: [
            { id: 101, name: 'Triple Treat Box', price: 'Rs. 2500', description: 'Two medium 1-topping pizzas, breadsticks, and a giant chocolate chip cookie.', image: '📦' },
            { id: 102, name: 'Weekday Lunch Special', price: 'Rs. 750', description: 'Personal pan pizza with one side and a drink. Available Monday-Friday, 11am-3pm.', image: '🍕' },
            { id: 103, name: 'Family Feast', price: 'Rs. 3200', description: 'Large pizza, garlic bread, wings, and 2L drink.', image: '🍕' }
          ],
          popular: [
            { id: 1, name: 'Super Supreme', price: 'Rs. 1400', description: 'Loaded with pepperoni, beef, mushrooms, black olives, onions, and green peppers.', image: '🍕' },
            { id: 2, name: 'Chicken Tikka', price: 'Rs. 1300', description: 'Spicy chicken tikka with onions and green peppers.', image: '🍕' },
            { id: 3, name: 'Cheese Lovers', price: 'Rs. 1200', description: 'Extra cheese on a classic pizza base.', image: '🍕' }
          ],
          pizza: [
            { id: 4, name: 'Super Supreme', price: 'Rs. 1400', description: 'Loaded with pepperoni, beef, mushrooms, black olives, onions, and green peppers.', image: '🍕', 
              variations: [
                { name: 'Size', options: ['Small (8")', 'Medium (10")', 'Large (12")', 'Extra Large (14")'] },
                { name: 'Crust', options: ['Pan', 'Thin & Crispy', 'Stuffed Crust', 'Cheese Burst'] },
                { name: 'Extra Toppings', options: ['Extra Cheese', 'Mushrooms', 'Onions', 'Peppers', 'Olives'] }
              ]
            },
            { id: 5, name: 'Chicken Tikka', price: 'Rs. 1300', description: 'Spicy chicken tikka with onions and green peppers.', image: '🍕',
              variations: [
                { name: 'Size', options: ['Small (8")', 'Medium (10")', 'Large (12")', 'Extra Large (14")'] },
                { name: 'Crust', options: ['Pan', 'Thin & Crispy', 'Stuffed Crust', 'Cheese Burst'] },
                { name: 'Extra Toppings', options: ['Extra Cheese', 'Mushrooms', 'Onions', 'Peppers', 'Olives'] }
              ]
            },
            { id: 6, name: 'Cheese Lovers', price: 'Rs. 1200', description: 'Extra cheese on a classic pizza base.', image: '🍕',
              variations: [
                { name: 'Size', options: ['Small (8")', 'Medium (10")', 'Large (12")', 'Extra Large (14")'] },
                { name: 'Crust', options: ['Pan', 'Thin & Crispy', 'Stuffed Crust', 'Cheese Burst'] }
              ]
            },
            { id: 7, name: 'Veggie Lovers', price: 'Rs. 1100', description: 'Mushrooms, black olives, onions, and green peppers.', image: '🍕',
              variations: [
                { name: 'Size', options: ['Small (8")', 'Medium (10")', 'Large (12")', 'Extra Large (14")'] },
                { name: 'Crust', options: ['Pan', 'Thin & Crispy', 'Stuffed Crust', 'Cheese Burst'] }
              ]
            }
          ],
          sides: [
            { id: 8, name: 'Garlic Bread', price: 'Rs. 450', description: 'Bread topped with garlic and herbs.', image: '🍞' },
            { id: 9, name: 'Buffalo Wings', price: 'Rs. 650', description: 'Spicy chicken wings.', image: '🍗' },
            { id: 10, name: 'Potato Wedges', price: 'Rs. 400', description: 'Seasoned potato wedges.', image: '🍟' }
          ],
          drinks: [
            { id: 11, name: 'Pepsi', price: 'Rs. 150', description: 'Classic Pepsi soft drink.', image: '🥤' },
            { id: 12, name: '7up', price: 'Rs. 150', description: 'Refreshing lemon-lime flavored soft drink.', image: '🥤' },
            { id: 13, name: 'Mountain Dew', price: 'Rs. 150', description: 'Citrus-flavored soft drink.', image: '🥤' }
          ],
          desserts: [
            { id: 14, name: 'Chocolate Lava Cake', price: 'Rs. 450', description: 'Warm chocolate cake with a molten chocolate center.', image: '🍰' },
            { id: 15, name: 'Cinnamon Sticks', price: 'Rs. 350', description: 'Sweet cinnamon-sugar breadsticks with icing.', image: '🥖' }
          ]
        }
      },
      'kfc': {
        name: 'KFC',
        rating: 4.1,
        deliveryTime: '20-30 min',
        priceRange: 'RsRs',
        categories: ['Fast Food', 'Fried Chicken'],
        image: '🍗',
        bgColor: 'bg-red-600',
        textColor: 'text-white',
        address: 'Shop #12, F-7 Markaz, Jinnah Super, Islamabad',
        phone: '+92 303 5555123',
        openingHours: {
          monday: '11:00 AM - 1:00 AM',
          tuesday: '11:00 AM - 1:00 AM',
          wednesday: '11:00 AM - 1:00 AM',
          thursday: '11:00 AM - 1:00 AM',
          friday: '11:00 AM - 2:00 AM',
          saturday: '11:00 AM - 2:00 AM',
          sunday: '12:00 PM - 12:00 AM'
        },
        description: 'KFC (Kentucky Fried Chicken) is an American fast food restaurant chain headquartered in Louisville, Kentucky, that specializes in fried chicken.',
        website: 'kfcpakistan.com',
        menuCategories: [
          { id: 'popular', name: 'Popular Items' },
          { id: 'chicken', name: 'Chicken' },
          { id: 'burgers', name: 'Burgers & Wraps' },
          { id: 'sides', name: 'Sides' },
          { id: 'drinks', name: 'Drinks' },
          { id: 'deals', name: 'Deals' }
        ],
        menu: {
          deals: [
            { id: 101, name: 'Mighty Bucket for One', price: 'Rs. 950', description: 'Chicken pieces, hot wings, popcorn chicken, fries, and a drink.', image: '🍗' },
            { id: 102, name: 'Family Feast', price: 'Rs. 2500', description: '8 pieces of chicken, 4 regular fries, and 1.5L drink.', image: '🍗' },
            { id: 103, name: 'Twister Combo', price: 'Rs. 800', description: 'Twister wrap, regular fries, and a drink.', image: '🌯' }
          ],
          popular: [
            { id: 1, name: 'Zinger Burger', price: 'Rs. 650', description: 'Spicy, crispy chicken fillet with lettuce and mayo on a sesame bun.', image: '🍔' },
            { id: 2, name: 'Hot Wings (6pcs)', price: 'Rs. 500', description: 'Spicy, crispy hot wings.', image: '🍗' },
            { id: 3, name: 'Mighty Zinger', price: 'Rs. 850', description: 'Our biggest, meatiest burger ever.', image: '🍔' }
          ],
          chicken: [
            { id: 4, name: 'Hot Wings (6pcs)', price: 'Rs. 500', description: 'Spicy, crispy hot wings.', image: '🍗' },
            { id: 5, name: 'Hot Wings (12pcs)', price: 'Rs. 900', description: 'Spicy, crispy hot wings.', image: '🍗' },
            { id: 6, name: 'Original Recipe (2pcs)', price: 'Rs. 550', description: 'Colonel\'s secret recipe of 11 herbs and spices.', image: '🍗' },
            { id: 7, name: 'Original Recipe (4pcs)', price: 'Rs. 950', description: 'Colonel\'s secret recipe of 11 herbs and spices.', image: '🍗' }
          ],
          burgers: [
            { id: 8, name: 'Zinger Burger', price: 'Rs. 650', description: 'Spicy, crispy chicken fillet with lettuce and mayo on a sesame bun.', image: '🍔' },
            { id: 9, name: 'Mighty Zinger', price: 'Rs. 850', description: 'Our biggest, meatiest burger ever.', image: '🍔' },
            { id: 10, name: 'Kentucky Burger', price: 'Rs. 700', description: 'Double chicken fillet burger with cheese.', image: '🍔' }
          ],
          sides: [
            { id: 11, name: 'French Fries', price: 'Rs. 300', description: 'Golden, crispy, and delicious potato fries.', image: '🍟' },
            { id: 12, name: 'Coleslaw', price: 'Rs. 150', description: 'Fresh cabbage and carrots in a creamy dressing.', image: '🥗' },
            { id: 13, name: 'Mashed Potatoes', price: 'Rs. 200', description: 'Creamy mashed potatoes with gravy.', image: '🥔' }
          ],
          drinks: [
            { id: 14, name: 'Pepsi', price: 'Rs. 150', description: 'Classic Pepsi soft drink.', image: '🥤' },
            { id: 15, name: '7up', price: 'Rs. 150', description: 'Refreshing lemon-lime flavored soft drink.', image: '🥤' },
            { id: 16, name: 'Mountain Dew', price: 'Rs. 150', description: 'Citrus-flavored soft drink.', image: '🥤' }
          ]
        }
      }
    };
    
    return restaurantMap[slug] || {
      name: 'Restaurant Not Found',
      rating: 0,
      deliveryTime: 'N/A',
      priceRange: 'N/A',
      categories: ['N/A'],
      image: '🍽️',
      bgColor: 'bg-gray-500',
      textColor: 'text-white',
      address: 'Not available',
      phone: 'Not available',
      openingHours: {},
      description: 'This restaurant could not be found.',
      website: '',
      menuCategories: [],
      menu: {}
    };
  };
  
  // Restaurant data (in a real app, you would fetch this)
  const restaurant = React.useMemo(() => {
    return getRestaurantData(resolvedParams?.slug);
  }, [resolvedParams]);
  
  // Generate SEO title based on restaurant data
  const pageTitle = React.useMemo(() => {
    if (!restaurant) return 'Restaurant | OpenMenu.pk';
    
    // Format: Restaurant Name Location – Cuisine Type | OpenMenu.pk
    if (restaurant.location && restaurant.categories?.[0]) {
      return `${restaurant.name} ${restaurant.location} – ${restaurant.categories[0]} | OpenMenu.pk`;
    }
    
    // Alternate format if location or category isn't available
    return `${restaurant.name} | Best Food Delivery | OpenMenu.pk`;
  }, [restaurant]);
  
  // Update document title and meta tags manually
  useEffect(() => {
    if (!restaurant) return;
    
    // Set document title
    document.title = pageTitle;
    
    // Set meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = "description";
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = `Order food online from ${restaurant?.name}. ${restaurant?.categories?.join(', ')} delivery in ${restaurant?.location || 'your city'}.`;
    
    // Add structured data
    let scriptLD = document.querySelector('script[type="application/ld+json"]');
    if (!scriptLD) {
      scriptLD = document.createElement('script');
      scriptLD.type = "application/ld+json";
      document.head.appendChild(scriptLD);
    }
    
    scriptLD.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Restaurant",
      "name": restaurant.name,
      "image": restaurant.image || "",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": restaurant.location || "Pakistan",
        "addressCountry": "Pakistan"
      },
      "priceRange": restaurant.priceRange || "$$",
      "servesCuisine": restaurant.categories?.join(", ") || "",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": restaurant.rating || "4.5",
        "reviewCount": "100"
      },
      "url": `https://openmenupk.com/restaurant/${resolvedParams.slug}`
    });
  }, [pageTitle, restaurant, resolvedParams]);
  
  // Price variations based on size selections (for pizzas)
  const sizeBasedPrices = {
    'Small (8")': 0,
    'Medium (10")': 200,
    'Large (12")': 400,
    'Extra Large (14")': 600
  };

  // Additional prices for crust selections
  const crustBasedPrices = {
    'Pan': 0,
    'Thin & Crispy': 50,
    'Stuffed Crust': 150,
    'Cheese Burst': 200
  };

  // Additional prices for extra toppings
  const toppingBasedPrices = {
    'Extra Cheese': 100,
    'Mushrooms': 70,
    'Onions': 50,
    'Peppers': 50,
    'Olives': 80
  };

  // Show toast message near button
  const showToast = (message, event) => {
    // Get position of clicked button if event is provided
    if (event && event.currentTarget) {
      const buttonRect = event.currentTarget.getBoundingClientRect();
      setToastPosition({
        x: buttonRect.left,
        y: buttonRect.top - 40 // Position toast above the button
      });
    }
    
    setToast({ show: true, message });
    setTimeout(() => setToast({ show: false, message: '' }), 2000);
  };
  
  // Calculate price based on variations
  useEffect(() => {
    if (!selectedItem) return;
    
    // Parse base price from string (e.g., "Rs. 1400")
    const priceString = selectedItem.price;
    const basePrice = parseInt(priceString.replace(/[^0-9]/g, ''));
    setBasePrice(basePrice);
    
    let additionalCost = 0;
    
    // Calculate additional costs based on selected variations
    if (selectedVariations) {
      Object.entries(selectedVariations).forEach(([key, value]) => {
        if (key === 'Size' && sizeBasedPrices[value]) {
          additionalCost += sizeBasedPrices[value];
        }
        
        if (key === 'Crust' && crustBasedPrices[value]) {
          additionalCost += crustBasedPrices[value];
        }
        
        // For extra toppings, we check if they are selected
        if (key === 'Extra Toppings' && value) {
          Object.entries(value).forEach(([topping, isSelected]) => {
            if (isSelected && toppingBasedPrices[topping]) {
              additionalCost += toppingBasedPrices[topping];
            }
          });
        }
      });
    }
    
    setCalculatedPrice(basePrice + additionalCost);
  }, [selectedItem, selectedVariations]);
  
  // Function to add item to cart
  const addItemToCart = (item, variations = {}, event = null) => {
    // Create a new cartItem with unique ID, selected variations, and quantity
    const cartItem = {
      ...item,
      // Use item id, current timestamp and a random number to ensure uniqueness
      id: `${item.id}-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
      variations: variations,
      quantity: itemQuantity,
      calculatedPrice: calculatedPrice || parseInt(item.price.replace(/[^0-9]/g, ''))
    };
    
    setFoodCart(prev => [...prev, cartItem]);
    setShowItemModal(false);
    setSelectedItem(null);
    setSelectedVariations({});
    setItemQuantity(1);
    showToast(`${item.name} added to your feast!`, event);
  };

  // Function to update item quantity in cart
  const updateCartItemQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    
    setFoodCart(prev => 
      prev.map(item => 
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      )
    );
  };
  
  // Function to remove item from cart
  const removeFromCart = (itemId) => {
    setFoodCart(prev => prev.filter(item => item.id !== itemId));
  };
  
  // Function to handle item click and show modal for variations if needed
  const handleItemSelect = (item, event) => {
    setSelectedItem(item);
    setItemQuantity(1);
    
    // Check if the item has variations
    if (item.variations && item.variations.length > 0) {
      // Initialize default selections
      const defaultVariations = {};
      item.variations.forEach(variation => {
        if (variation.name === 'Extra Toppings') {
          // For toppings, create an object with all toppings set to false initially
          const toppingsObj = {};
          variation.options.forEach(option => {
            toppingsObj[option] = false;
          });
          defaultVariations[variation.name] = toppingsObj;
        } else {
          defaultVariations[variation.name] = variation.options[0];
        }
      });
      
      setSelectedVariations(defaultVariations);
      setShowItemModal(true);
    } else {
      // No variations, add directly to cart
      addItemToCart(item, {}, event);
    }
  };
  
  // Function to render menu item
  const renderMenuItem = (item) => (
    <div key={item.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100 overflow-hidden p-4">
      <div className="flex">
        <div className={`${restaurant.bgColor} ${restaurant.textColor} flex items-center justify-center rounded-lg text-2xl p-3 mr-4 w-12 h-12 flex-shrink-0`}>
          {item.image}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start">
            <h3 className="font-semibold text-lg text-gray-800 line-clamp-1">{item.name}</h3>
            <span className="font-bold text-gray-800 ml-2 whitespace-nowrap">{item.price}</span>
          </div>
          <p className="text-sm text-gray-500 mt-1 line-clamp-2 md:line-clamp-none">{item.description}</p>
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <button 
          onClick={(e) => handleItemSelect(item, e)} 
          className="mt-1 bg-primary text-white rounded-md px-4 py-1.5 text-sm font-medium hover:bg-opacity-90 transition-colors active:bg-opacity-80 active:transform active:scale-95 touch-action-manipulation"
        >
          Add to Feast
        </button>
      </div>
    </div>
  );
  
  // Function to format opening hours
  const formatOpeningHours = () => {
    if (!restaurant.openingHours || typeof restaurant.openingHours === 'string') {
      return <p className="text-sm text-gray-600">{restaurant.openingHours || 'Not available'}</p>;
    }
    
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDayIndex = new Date().getDay(); // 0 is Sunday, 1 is Monday, etc.
    const currentDay = days[currentDayIndex];
    
    // Format for displaying the day name
    const formatDayName = (day) => {
      const shortNames = {
        'monday': 'Mon', 
        'tuesday': 'Tue', 
        'wednesday': 'Wed', 
        'thursday': 'Thu', 
        'friday': 'Fri', 
        'saturday': 'Sat', 
        'sunday': 'Sun'
      };
      return shortNames[day] || day.slice(0,3).charAt(0).toUpperCase() + day.slice(0,3).slice(1);
    };

    return (
      <div className="text-sm text-gray-600">
        {/* Current day's hours */}
        <div 
          className="flex justify-between items-center mb-1 cursor-pointer"
          onClick={() => setShowAllHours(!showAllHours)}
        >
          <div className="flex items-center">
            <span className="font-medium capitalize">{formatDayName(currentDay)}</span>
            <span className="ml-2 text-primary">(Today)</span>
          </div>
          <div className="flex items-center">
            <span className="mr-2">{restaurant.openingHours[currentDay] || 'Closed'}</span>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className={`h-4 w-4 transition-transform ${showAllHours ? 'rotate-180' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        
        {/* All days' hours */}
        {showAllHours && (
          <div className="mt-2 border-t pt-2 border-gray-100">
            {days.map(day => {
              if (day === currentDay) return null; // Skip current day as it's already shown
              return (
                <div key={day} className="flex justify-between py-1">
                  <span className="font-medium">{formatDayName(day)}</span>
                  <span>{restaurant.openingHours[day] || 'Closed'}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Function to render variation modal
  const renderVariationModal = () => {
    if (!showItemModal || !selectedItem) return null;
    
    return (
      <div className="fixed inset-0 flex items-end md:items-center justify-center z-50 bg-black bg-opacity-50 p-4">
        <div className="bg-white rounded-t-lg md:rounded-lg shadow-lg w-full max-w-lg md:max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 z-10 px-4 md:px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-white">
            <h3 className="text-lg md:text-xl font-bold text-gray-800">{selectedItem.name}</h3>
            <button 
              onClick={() => setShowItemModal(false)} 
              className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="px-4 md:px-6 py-4">
            <div className="flex items-center mb-4">
              <div className={`${restaurant.bgColor} ${restaurant.textColor} flex items-center justify-center rounded-lg text-2xl p-3 mr-4 w-12 h-12`}>
                {selectedItem.image}
              </div>
              <div>
                <p className="text-sm text-gray-600">{selectedItem.description}</p>
                <p className="text-primary font-bold mt-1">
                  {calculatedPrice ? `Rs. ${calculatedPrice}` : selectedItem.price}
                  {basePrice !== calculatedPrice && basePrice > 0 && (
                    <span className="text-gray-400 ml-2 text-xs line-through">Rs. {basePrice}</span>
                  )}
                </p>
              </div>
            </div>
            
            {/* Quantity selector */}
            <div className="mb-4">
              <h4 className="font-medium text-gray-800 mb-2">Quantity</h4>
              <div className="flex items-center">
                <button 
                  onClick={() => setItemQuantity(prev => Math.max(1, prev - 1))}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-l-md active:bg-gray-300 touch-action-manipulation"
                >
                  -
                </button>
                <span className="bg-gray-100 px-6 py-2">{itemQuantity}</span>
                <button 
                  onClick={() => setItemQuantity(prev => prev + 1)}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-r-md active:bg-gray-300 touch-action-manipulation"
                >
                  +
                </button>
              </div>
            </div>
            
            {selectedItem.variations && selectedItem.variations.map((variation, idx) => (
              <div key={idx} className="mb-5">
                <h4 className="font-medium text-gray-800 mb-2">{variation.name}</h4>
                
                {variation.name === 'Extra Toppings' ? (
                  // Render checkboxes for toppings
                  <div className="space-y-3">
                    {variation.options.map((option, optionIdx) => (
                      <div key={optionIdx} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id={`${variation.name}-${optionIdx}`}
                            checked={selectedVariations[variation.name]?.[option] || false}
                            onChange={() => {
                              const currentToppings = {...selectedVariations[variation.name]};
                              currentToppings[option] = !currentToppings[option];
                              setSelectedVariations({
                                ...selectedVariations, 
                                [variation.name]: currentToppings
                              });
                            }}
                            className="mr-2 h-5 w-5"
                          />
                          <label htmlFor={`${variation.name}-${optionIdx}`} className="text-gray-700">{option}</label>
                        </div>
                        {toppingBasedPrices[option] && (
                          <span className="text-xs text-gray-500">+Rs. {toppingBasedPrices[option]}</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  // Render radio buttons for other variations
                  <div className="space-y-3">
                    {variation.options.map((option, optionIdx) => (
                      <div key={optionIdx} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id={`${variation.name}-${optionIdx}`}
                            name={variation.name}
                            value={option}
                            checked={selectedVariations[variation.name] === option}
                            onChange={() => setSelectedVariations({...selectedVariations, [variation.name]: option})}
                            className="mr-2 h-5 w-5"
                          />
                          <label htmlFor={`${variation.name}-${optionIdx}`} className="text-gray-700">{option}</label>
                        </div>
                        {variation.name === 'Size' && sizeBasedPrices[option] > 0 && (
                          <span className="text-xs text-gray-500">+Rs. {sizeBasedPrices[option]}</span>
                        )}
                        {variation.name === 'Crust' && crustBasedPrices[option] > 0 && (
                          <span className="text-xs text-gray-500">+Rs. {crustBasedPrices[option]}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="sticky bottom-0 z-10 px-4 md:px-6 py-4 border-t border-gray-200 flex justify-between items-center bg-white">
            <div>
              <span className="text-gray-700 font-medium">Total: </span>
              <span className="text-primary font-bold">Rs. {calculatedPrice * itemQuantity}</span>
            </div>
            <button
              onClick={(e) => addItemToCart(selectedItem, selectedVariations, e)}
              className="bg-primary text-white rounded-md px-5 py-2.5 font-medium hover:bg-opacity-90 transition-colors active:bg-opacity-80 active:transform active:scale-95 touch-action-manipulation"
            >
              Add to Feast
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // Function to handle showing the cart modal
  const toggleCartModal = () => {
    setShowCartModal(!showCartModal);
  };

  // Function to handle the mini cart click 
  const handleMiniCartClick = () => {
    if (foodCart.length > 0) {
      toggleCartModal();
    }
  };

  // Update handleProceedToCheckout to first show cart modal
  const handleProceedToCheckout = () => {
    if (!showCartModal) {
      toggleCartModal();
      return;
    }
    
    // First transfer the cart items to the checkout context
    const restaurant = getRestaurantData(resolvedParams.slug);
    
    // Add each item from foodCart to the checkout context
    foodCart.forEach(item => {
      addToCart(
        {
          id: item.id,
          name: item.name,
          price: item.price,
          image: item.image || '/images/default-food.png',
          calculatedPrice: item.calculatedPrice
        },
        {
          id: resolvedParams.slug,
          name: restaurant.name,
          image: restaurant.image || '/images/default-restaurant.png',
          address: restaurant.address
        },
        item.variations || {},
        item.quantity
      );
    });
    
    // Navigate to checkout page
    router.push('/checkout');
  };

  // Function to render cart modal
  const renderCartModal = () => {
    if (!showCartModal || foodCart.length === 0) return null;
    
    const cartTotal = foodCart.reduce((sum, item) => {
      return sum + (item.calculatedPrice * item.quantity);
    }, 0);
    
    return (
      <div className="fixed inset-0 flex items-end md:items-center justify-center z-50 bg-black bg-opacity-50 p-4">
        <div className="bg-white rounded-t-lg md:rounded-lg shadow-lg w-full max-w-lg md:max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 z-10 px-4 md:px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-white">
            <h3 className="text-lg md:text-xl font-bold text-gray-800">Your Order</h3>
            <button 
              onClick={toggleCartModal} 
              className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="px-4 md:px-6 py-4">
            <div className="flex items-center mb-4">
              <div className={`${restaurant.bgColor} ${restaurant.textColor} flex items-center justify-center rounded-lg text-2xl p-3 mr-4 w-12 h-12`}>
                {restaurant.image}
              </div>
              <div>
                <h4 className="font-bold">{restaurant.name}</h4>
                <p className="text-sm text-gray-600 truncate">{restaurant.address}</p>
              </div>
            </div>
            
            <div className="divide-y divide-gray-100">
              {foodCart.map(item => (
                <div key={item.id} className="py-4">
                  <div className="flex flex-col sm:flex-row">
                    <div className="flex flex-1 mb-3 sm:mb-0">
                      <div className={`${restaurant.bgColor} ${restaurant.textColor} flex items-center justify-center rounded-lg text-xl p-2 mr-3 w-10 h-10 flex-shrink-0`}>
                        {item.image}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h5 className="font-medium text-gray-800">{item.name}</h5>
                        
                        {Object.keys(item.variations || {}).length > 0 && (
                          <div className="text-xs text-gray-500 mt-1">
                            {Object.entries(item.variations).map(([key, value]) => {
                              if (!value) return null;
                              
                              if (typeof value === 'object') {
                                // Handle extra toppings object
                                const selectedToppings = Object.entries(value)
                                  .filter(([_, selected]) => selected)
                                  .map(([topping]) => topping);
                                
                                if (selectedToppings.length === 0) return null;
                                
                                return (
                                  <p key={key}>
                                    <span className="font-medium">{key}:</span> {selectedToppings.join(', ')}
                                  </p>
                                );
                              }
                              
                              return (
                                <p key={key}>
                                  <span className="font-medium">{key}:</span> {value}
                                </p>
                              );
                            })}
                          </div>
                        )}
                        
                        <p className="text-primary font-medium text-sm mt-1">Rs. {item.calculatedPrice}</p>
                      </div>
                    </div>
                    
                    <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-start">
                      <button 
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-500 text-xs mb-0 sm:mb-2 hover:text-red-700 px-2 py-1 hover:bg-gray-50 rounded"
                      >
                        Remove
                      </button>
                      
                      <div className="flex items-center border border-gray-200 rounded h-8">
                        <button 
                          onClick={() => updateCartItemQuantity(item.id, item.quantity - 1)}
                          className="w-8 h-8 flex items-center justify-center bg-gray-100 active:bg-gray-200 touch-action-manipulation"
                          aria-label="Decrease quantity"
                        >
                          <span className="text-gray-700">-</span>
                        </button>
                        <span className="w-8 h-8 flex items-center justify-center">{item.quantity}</span>
                        <button 
                          onClick={() => updateCartItemQuantity(item.id, item.quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center bg-gray-100 active:bg-gray-200 touch-action-manipulation"
                          aria-label="Increase quantity"
                        >
                          <span className="text-gray-700">+</span>
                        </button>
                      </div>
                      
                      <p className="text-sm font-bold hidden sm:block mt-2">Rs. {item.calculatedPrice * item.quantity}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="sticky bottom-0 z-10 px-4 md:px-6 py-4 border-t border-gray-200 bg-white">
            <div className="flex justify-between font-bold text-lg mb-4">
              <span>Total</span>
              <span>Rs. {cartTotal}</span>
            </div>
            
            <button
              className="w-full bg-primary text-white rounded-md px-4 py-3 font-medium hover:bg-opacity-90 transition-colors active:bg-opacity-80 active:transform active:scale-95 touch-action-manipulation"
              onClick={handleProceedToCheckout}
            >
              Proceed to Checkout
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // Function to render Mini Cart
  const renderMiniCart = () => {
    if (foodCart.length === 0) return null;
    
    const totalItems = foodCart.reduce((sum, item) => sum + item.quantity, 0);
    const cartTotal = foodCart.reduce((sum, item) => {
      return sum + (item.calculatedPrice * item.quantity);
    }, 0);
    
    return (
      <div className="fixed bottom-20 md:bottom-6 left-0 md:left-auto right-0 md:right-6 z-40 px-4 md:px-0">
        <button 
          className="w-full md:w-auto bg-primary text-white rounded-lg md:rounded-full p-4 shadow-lg flex items-center justify-between md:justify-center font-medium"
          onClick={handleMiniCartClick}
        >
          <div className="flex items-center">
            <span className="mr-2">🍽️</span>
            <span>{totalItems} {totalItems === 1 ? 'item' : 'items'}</span>
          </div>
          <div className="flex items-center">
            <span className="mx-2 hidden md:inline">•</span>
            <span>Rs. {cartTotal}</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </button>
      </div>
    );
  };

  // Toast notification
  const renderToast = () => {
    if (!toast.show) return null;
    
    // Calculate positioning styles
    const style = {
      transform: 'translate(-50%, 0)',
      left: `${toastPosition.x}px`,
      top: `${toastPosition.y}px`
    };
    
    return (
      <div 
        className="fixed z-50 bg-white text-primary px-4 py-2 rounded-lg shadow-md border border-primary text-sm animate-fadeIn"
        style={style}
      >
        <span>✅ {toast.message}</span>
      </div>
    );
  };

  // Menu categories section rendering
  const renderCategoryNav = () => {
    return (
      <div className="mb-4 relative">
        {/* Categories scrollable area with improved mobile handling */}
        <div className="overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
          <div className="flex space-x-2 min-w-max">
            {restaurant.menuCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-colors duration-200 ease-in-out ${
                  activeCategory === category.id
                    ? 'bg-primary text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50 min-w-[4rem] h-10 flex items-center justify-center touch-manipulation`}
              >
                {category.id === 'deals' && <span className="mr-1">🔥</span>}
                {category.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Shadow indicators for scroll - only shown when content is scrollable */}
        <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-white to-transparent pointer-events-none md:hidden"></div>
        <div className="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-white to-transparent pointer-events-none md:hidden"></div>
      </div>
    );
  };

  // Modify the food item card rendering for better mobile layout
  const renderFoodItem = (item) => {
    return (
      <div 
        key={item.id} 
        className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer mb-3"
        onClick={() => handleItemSelect(item)}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center">
          {/* Food image - adjusted sizing for mobile */}
          <div className="relative w-full sm:w-24 h-32 sm:h-24 rounded-lg overflow-hidden mb-3 sm:mb-0 sm:mr-3 bg-gray-100 flex-shrink-0">
            {item.image && (item.image.startsWith('/') || item.image.startsWith('http')) ? (
              <img 
                src={item.image || '/images/default-food.png'} 
                alt={item.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-3xl">
                {item.image || item.emoji || '🍔'}
              </div>
            )}
          </div>
          
          {/* Food details - better spacing on mobile */}
          <div className="flex-1 flex flex-col">
            <div className="flex justify-between items-start w-full">
              <h3 className="text-lg font-medium text-gray-800">{item.name}</h3>
              <span className="font-bold text-primary">{item.price}</span>
            </div>
            
            <p className="text-sm text-gray-600 line-clamp-2 mt-1 mb-2">{item.description}</p>
            
            {/* Add to cart button - larger touch target */}
            <button 
              className="mt-auto py-2 px-4 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary-dark transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50 self-start"
              onClick={(e) => handleItemSelect(item, e)}
            >
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={`Order food online from ${restaurant?.name}. ${restaurant?.categories?.join(', ')} delivery in ${restaurant?.location || 'your city'}.`} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Restaurant",
              "name": restaurant.name,
              "image": restaurant.image || "",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": restaurant.location || "Pakistan",
                "addressCountry": "Pakistan"
              },
              "priceRange": restaurant.priceRange || "$$",
              "servesCuisine": restaurant.categories?.join(", ") || "",
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": restaurant.rating || "4.5",
                "reviewCount": "100"
              },
              "url": `https://openmenupk.com/restaurant/${resolvedParams.slug}`
            })
          }}
        />
      </Head>
      
      <main className="flex min-h-screen">
        {/* Sidebar - Hidden on mobile */}
        <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
        
        {/* Mobile Header - Visible only on mobile */}
        <MobileHeader setMobileOpen={setMobileOpen} />
        
        <div className="flex-1 h-screen overflow-y-auto bg-gray-50">
          {/* Restaurant Header Banner */}
          <div className={`${restaurant.bgColor} ${restaurant.textColor} px-4 md:px-8 py-6 md:py-8 relative mt-14 md:mt-0`}>
            <div className="max-w-4xl mx-auto relative">
              <Link href="/" className="hidden md:flex absolute top-0 left-0 items-center hover:opacity-80 transition-opacity bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="font-medium">Back</span>
              </Link>
              
              <div className="text-center pt-0 md:pt-3">
                <div className="text-4xl md:text-5xl mb-3 inline-block bg-white/20 p-3 md:p-4 rounded-full backdrop-blur-sm">
                  {restaurant.image}
                </div>
                <h1 className="text-2xl md:text-3xl font-bold mb-2">{restaurant.name}</h1>
                <div className="flex items-center justify-center text-sm space-x-3">
                  <span className="flex items-center">
                    ⭐ {restaurant.rating}
                  </span>
                  <span>•</span>
                  <span>{restaurant.deliveryTime}</span>
                  <span>•</span>
                  <span>{restaurant.priceRange}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Restaurant Details */}
          <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 bg-white shadow-sm -mt-6 rounded-t-xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-700">Address</h3>
                <p className="text-sm text-gray-600">{restaurant.address}</p>
              </div>
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-700">Opening Hours</h3>
                {formatOpeningHours()}
              </div>
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-700">Contact</h3>
                <p className="text-sm text-gray-600">{restaurant.phone}</p>
                {restaurant.website && (
                  <a href={`https://${restaurant.website}`} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline block">
                    {restaurant.website}
                  </a>
                )}
              </div>
            </div>
            
            <div className="mb-8">
              <h3 className="font-semibold text-gray-700 mb-2">About</h3>
              <p className="text-sm text-gray-600">{restaurant.description}</p>
            </div>
            
            {/* Menu Categories Navigation - Scrollable on mobile */}
            {renderCategoryNav()}
            
            {/* Menu Items - Responsive grid */}
            <div className="space-y-4 mb-20">
              {restaurant.menu[activeCategory]?.map(renderFoodItem) || (
                <div className="text-center text-gray-500 py-8">
                  No menu items found in this category
                </div>
              )}
            </div>
            
            {/* Order Call to Action */}
            <div className="bg-gray-50 rounded-lg p-6 text-center mb-24 relative z-10">
              <h3 className="text-xl font-bold text-gray-800 mb-2">Ready to Order?</h3>
              <p className="text-gray-600 mb-4">Place your order now and enjoy delicious food from {restaurant.name}</p>
              <button className="bg-primary text-white rounded-lg px-6 py-3 font-medium hover:bg-opacity-90 transition-colors">
                Start Your Feast
              </button>
            </div>
          </div>
          
          {/* Mini Cart */}
          {renderMiniCart()}
          
          {/* Variation Modal */}
          {renderVariationModal()}
          
          {/* Cart Modal */}
          {renderCartModal()}
          
          {/* Toast Notification */}
          {renderToast()}
          
          {/* Bottom search prompt with lower z-index */}
          <div className="relative z-0">
            <SearchPrompt />
          </div>
        </div>
        
        {/* Mobile Bottom Navigation */}
        <MobileNavigation />
      </main>
    </>
  );
} 